/* What the current session is allowed to do.

   Two different kinds of limit live here, and it's worth being honest about
   which is which. Cloud storage limits (project count, storage used, upload
   size) are real limits — row-level security and storage policies enforce
   them server-side, the UI is just where you find out early. The export
   watermark is not that: the render happens in the browser, canvas to PNG,
   so it cannot be genuinely withheld — a determined person can always
   right-click the canvas. It's a nudge, same as it always was; what changed
   is that hasCredits() now reads a real, server-owned balance
   (services/entitlements.js) instead of a hardcoded table, so the nudge is
   now backed by something a client can't just grant itself (see
   0004_billing.sql's RLS). */

import { emit } from '../state/bus.js';
import { usage } from './assets.js';
import { isSignedIn, isAnonymous } from './auth.js';
import { creditBalance, hasActivePack, hasEverPurchased } from './entitlements.js';
import { motionActive } from './motion.js';

/* Cloud project storage stays a real-account feature — unrelated to credits,
   unchanged from before anonymous auth existed. An anonymous session is a
   real row with real RLS, but it has nothing to recover it with if it's
   lost, which is the wrong shape for something meant to be a durable save.

   There is no cap on how many. There used to be one (three), from when
   projects were only ever the thing you were working on — but projects
   absorbed presets, so the list is now also the user's library of starting
   points, and three is not a library. Storage is still bounded by the real
   server-enforced limits below. */
const PROJECT_LIMIT = { storageBytes: 200 * 1024 * 1024, maxUpload: 25 * 1024 * 1024 };
const hasRealAccount = () => isSignedIn() && !isAnonymous();

/* THE WATERMARK HAS NO FREE WAY OFF.

   There used to be a "Remove watermark (for 1 hour)" checkbox here: an
   honour-system declaration that the work was non-profit or personal, taking
   the mark off for a session. It was an honest idea and it made the free tier
   genuinely usable, but it sat directly across the thing credits are sold for
   — DEPLOY.md flagged exactly that before launch and asked for the choice to
   be made deliberately rather than inherited. It has been: a clean file is
   paid for, by a credit or by a licence, and nothing else takes the mark off.

   What this does NOT claim is that the mark cannot be removed. The render
   happens in the browser and always did; see the note at the top of this
   file. The point is that the app no longer offers to do it for you. */

/* WHAT AN EXPORT COSTS, in credits.
 *
 * A still costs nothing. That is the free tier doing what the pricing page
 * says it does — photograph, calibrate, design, export a PNG — and a free
 * export is a CLEAN one: resolveWatermark() treats a cost of zero as "no mark
 * to sell", so nothing marks a still anywhere, and there is nothing for a
 * credit to buy off it either.
 *
 * Video is what credits are for. A thirty-second render is not the same thing
 * to produce as one frame, and the panelled version encodes a file per screen.
 * The numbers live here rather than at the call sites because the pricing is
 * one decision, and three copies of it is three things to find when it
 * changes.
 *
 * The on-site clip is the cheap one deliberately: it is a mockup for showing
 * somebody what the piece will look like on the building, at the size of the
 * photograph rather than the screen's own resolution — a thing you make
 * several of while pitching, not the file that goes on the display.
 *
 * THE FIVE VIDEO PRICES MUST STAY DISTINCT. ui/settings.js reads the credit
 * history back and names each row by the amount it cost, so two equal prices
 * would make a ledger nobody can read.
 *
 * The server charges these, not the client: consume_export_credits(n) in
 * 0026 does the guard and the subtraction in one statement. These constants
 * are what the app SAYS it will cost, so the export cards can price
 * themselves honestly before anyone commits to a two-minute encode. */
export const EXPORT_COST = { still: 0, video: 10, videoPanels: 20, onsiteVideo: 5, unwrapVideo: 15, unwrapPanels: 30 };

/* THE UNWRAP TOOL'S INPUT (app.illusio3d.com/unwrap). A filmed clip of a screen
   from an angle is not a clip added to a scene, so MEDIA_LIMIT below — 25 MB and a
   minute, sized for assets that live inside a project and are uploaded with it —
   does not apply. Nothing here is stored or uploaded: the file is read in place
   from disk, decoded frame by frame and encoded once, so the limits are about what
   a browser tab can encode, not about storage. A phone recording of a screen is
   routinely 100-400 MB. */
export const UNWRAP_LIMIT = { bytes: 1024 * 1024 * 1024, seconds: 300, longSide: 3840 };

/** Whether the balance covers a given export — for what the UI shows. The
 *  actual refusal is the server's, atomically, at the moment of spending. */
export const canAfford = (cost) => creditBalance() >= cost;

/* THE WATERMARK IS A CHOICE, MADE ON THE EXPORT SCREEN, AND ON BY DEFAULT.
 *
 * On: a video exports free, with the moving mark. Off: a clean video, paid for
 * with credits or covered by a licence. That is the whole bargain and it is
 * put to the person rather than inferred from what happens to be in their
 * account — which is what it used to be, and it is why the mark could vanish
 * from the preview for the very people looking after the app: a licence or a
 * spare ten credits silently turned it off, and nothing said so.
 *
 * Per session and NOT stored anywhere, deliberately. Off is the choice that
 * spends money, so it should be made again on purpose each time rather than
 * carried over from a Tuesday into a Thursday's export. On is the safe side to
 * fall back to.
 *
 * It is one switch for everything that shows or applies the mark: the export
 * (export/png.js's resolveWatermark), the Flat and Split preview
 * (ui/chrome.js) and the Export screen's own buttons. Three answers to one
 * question is how the preview came to disagree with the file. */
let videoMark = true;
export const videoWatermarkOn = () => videoMark;
export function setVideoWatermark(on) {
  videoMark = !!on;
  emit('export:mark', videoMark);
}

/** Whether a video export would carry the mark — the fallback's question, for
 *  when somebody has asked for a clean file and cannot pay for one. The
 *  preview asks videoWatermarkOn() instead: it shows what was CHOSEN, not
 *  what a failed payment would fall back to. */
export function watermarkExports() {
  if (videoMark) return true;
  return !motionActive() && !canAfford(EXPORT_COST.video);
}

const mb = n => Math.round(n / (1024 * 1024)) + ' MB';

/** @returns {{ok:boolean, reason?:string}} */
export async function canUpload(bytes) {
  if (!hasRealAccount()) return { ok: false, reason: 'Sign in to store files in the cloud.' };
  if (bytes > PROJECT_LIMIT.maxUpload)
    return { ok: false, reason: `That file is ${mb(bytes)}. The limit is ${mb(PROJECT_LIMIT.maxUpload)}.` };
  const used = await usage();
  if (used.bytes + bytes > PROJECT_LIMIT.storageBytes)
    return { ok: false, reason: `That would put you over your ${mb(PROJECT_LIMIT.storageBytes)} of storage.` };
  return { ok: true };
}

/* What a clip is allowed to be.

   Three separate ceilings because they bound three different costs, and file
   size does not imply the other two — a well-compressed 4-minute 4K clip can
   be smaller than a badly-compressed 10-second one, while costing far more to
   decode. Bytes bound storage and upload time; seconds and resolution bound
   what the decoder has to do sixty times a second while someone scrubs.

   The byte cap is deliberately the SAME number as PROJECT_LIMIT.maxUpload, so
   the local path can never accept a file the cloud would later refuse — the
   actual bug this prevents is a clip that works fine until the day you sign
   in, and then silently fails to save. */
export const MEDIA_LIMIT = { bytes: 25 * 1024 * 1024, seconds: 60, longSide: 3840 };

/**
 * Whether a probed clip can be added at all — local or cloud, signed in or
 * not. The decoder and the browser's memory do not care whether there is an
 * account; canUpload() above is the separate, additional question of cloud
 * quota, asked only when there is somewhere to upload to.
 *
 * @param {{bytes:number, seconds:number, width:number, height:number}} clip
 * @returns {{ok:boolean, reason?:string}}
 */
export function canAddVideo({ bytes, seconds, width, height }) {
  /* THE LICENCE FIRST, before any of the measurements: someone who may not add
     a clip at all does not need to hear that this particular clip is four
     seconds too long, and fixing that would not help them.

     Asked HERE rather than on the Video button, because the button is one of
     three ways in — drag and drop is another, and swapping the file on an
     existing layer is a third. Every one of them funnels through this
     function, which is the whole reason the gate belongs in it. */
  if (!canUseVideo().ok) return canUseVideo();
  if (bytes > MEDIA_LIMIT.bytes)
    return { ok: false, reason: `That clip is ${mb(bytes)}. The limit is ${mb(MEDIA_LIMIT.bytes)} — trim it or export it at a lower bitrate.` };
  if (seconds > MEDIA_LIMIT.seconds)
    return { ok: false, reason: `That clip is ${Math.round(seconds)} seconds. The limit is ${MEDIA_LIMIT.seconds} — signage loops are short, and a long clip is slow to scrub and slower to export.` };
  const longSide = Math.max(width, height);
  if (longSide > MEDIA_LIMIT.longSide)
    return { ok: false, reason: `That clip is ${width} × ${height}. The limit is ${MEDIA_LIMIT.longSide} on the long side — 4K is already more than a plane in a render needs.` };
  return { ok: true };
}

/** The premium half of the template library. Unlocked for the 12 months a
 *  credit pack runs (0030), same as video export and cloud projects — not
 *  re-locked when credits run out, only when the 12 months do.
 *
 *  DORMANT: the panel this gates (ui/templates.js, opened by #btnTemplates) has
 *  the `hidden` class in index.html and nothing removes it, so no user can reach
 *  a locked template today. It is deliberately NOT advertised anywhere — the
 *  credits dialog used to promise "the full template library" as a benefit of
 *  buying a pack, which promised something nobody could see. Un-hide the
 *  button and this becomes live again; put the promise back at the same time.
 *  @returns {{ok:boolean, reason?:string}} */
export function canUseLockedTemplates() {
  /* A licence unlocks them too. Credits are not shown to a licence holder at
     all (ui/billing.js, ui/settings.js), so leaving this to a pack alone would
     strand them with locked templates and a call to action they can no longer
     see — the one door out being a credit pack, on an account that has been
     deliberately shown none. */
  return (hasLicence() || hasActivePack())
    ? { ok: true }
    : { ok: false, reason: 'Any credit pack unlocks the full template library for 12 months.' };
}

/* Said to somebody whose pack's 12 months have run out, in place of the
   never-bought wording — they know what a pack is, and telling them it
   "unlocks" something reads as if the app had forgotten they bought one. The
   date itself is in Settings; a one-line reason has no room for it. */
const LAPSED = 'Your credit pack’s 12 months have ended — any new pack unlocks it again for another 12.';
const lapsedOr = (neverBought) => (hasEverPurchased() ? LAPSED : neverBought);

/* Sharing is the one tool still behind the Advanced licence — see
   canShareProjects. Same pricing clause the other licence sentences used, and
   it names the capability the way the person just met it: "sharing a project",
   not "collaboration", which is a word for a category rather than for the
   button they pressed. */
export const SHARE_LOCKED_REASON =
  'Sharing a project with someone else is included in the Advanced plan. Licensing terms '
  + 'are worked out individually for each customer.';

/* What somebody is told when they ask for a video file without having paid for
   anything — purely about VIDEO, on purpose. This is also what mp4.js throws
   as an Error message and multipanel.js returns as a gate reason, neither of
   which is a moment to say anything about stills; it used to end "Stills are
   always free," which read fine on the Export screen and oddly everywhere
   else. The Export screen composes its own, fuller sentence about stills
   (see export-view.js) — commercial use is a real distinction there
   ("free for non-commercial use" vs. what a pack adds) that this one-line
   reason has no room to carry accurately anyway. */
export const VIDEO_EXPORT_LOCKED_REASON =
  'Video export unlocks with any credit pack — for 12 months, watermarked video is free and '
  + 'credits take the watermark off.';

/* The call to action, kept apart from the sentence rather than embedded in
   it, because the sentence has to survive being put somewhere no control can
   go — a title attribute on the locked tab, and the message of the Error the
   export backstops throw. Only the surfaces that are real DOM (the toast, the
   timeline dock's explainer) offer the button.

   Plain "Contact us", the same door for every refusal that offers it — a
   lapsed licence blocking sharing, a video export with no credits, and
   whatever a re-locked Motion/array/video gate might need later. There is no
   trial to request any more (every tool is free to try without an account),
   so this no longer says "Try" or names a plan; it opens ui/trial.js's one
   contact form and the answer comes from a person, later.

   This used to link to another domain, which threw away everything the app
   already knew about who was asking. See ui/trial.js. */
export const MOTION_CTA = 'Contact us →';

/* The published privacy policy, on the landing site rather than in the app —
   the same document a visitor reaches from the footer, so there is one page to
   keep accurate rather than two that can disagree. Defined here because two
   surfaces link to it and a hard-coded URL in each is how one of them ends up
   pointing at a page that moved. */
export const PRIVACY_URL = 'https://illusio3d.com/privacy';

/** EVERY TOOL IS OPEN. The animation stage, its timeline and moves, screen
 *  arrays, and adding a clip are available to everybody, signed in or not, paid
 *  or not.
 *
 *  This used to be the Advanced licence, and the change is a deliberate one:
 *  the thing that is paid for is no longer the ability to USE the tools but
 *  what leaves the app. Somebody can build the whole animation for nothing and
 *  see it play; the price appears when they want the video file, and then only
 *  as a watermark they can take off. Stills, which is what most people came
 *  for, stay free and clean throughout.
 *
 *  The three functions stay rather than being deleted, with their call sites
 *  in the stage switch, the timeline dock, the moves panel and the clip
 *  importer. They are where a gate returns if one is ever wanted again, and
 *  pulling out fifteen call sites to save five lines here would be a large
 *  edit to reverse a decision that has already been reversed once.
 *  @returns {{ok:boolean, reason?:string}} */
export const canUseMotion = () => ({ ok: true });
export const canUseScreenArray = () => ({ ok: true });
export const canUseVideo = () => ({ ok: true });

/** Whether this account holds the Advanced licence. NOT a gate on any tool any
 *  more — it is what makes a clean video free (a licence covers what a credit
 *  would buy) and what sharing needs. Named for what it is, so the places that
 *  ask it are not reading like they are still asking whether Motion is allowed. */
export const hasLicence = () => motionActive();

/** EXPORTING A VIDEO FILE — the thing that is actually paid for.
 *
 *  Unlocked by any credit pack for 12 months from when credits were last added
 *  (0030) — spending them does not end it early: somebody who has paid should
 *  not find it taken off them because they used what they bought. An
 *  owner-granted balance counts the same as a purchase. The Advanced licence
 *  unlocks it too.
 *
 *  What this unlocks is the WATERMARKED video, free and without limit. Taking
 *  the mark off is a separate matter and costs credits — see resolveWatermark
 *  in export/png.js. Stills are not asked about at all: they are free.
 *  @returns {{ok:boolean, reason?:string}} */
export function canExportVideoFiles() {
  return (hasLicence() || hasActivePack())
    ? { ok: true }
    : { ok: false, reason: lapsedOr(VIDEO_EXPORT_LOCKED_REASON) };
}

/** SHARING a project with somebody else, and the taking-turns editing that
 *  comes with it.
 *
 *  The Advanced licence only — deliberately NOT unlocked by credits, and this
 *  is the one place that differs from canUseVideo() above. Credits buy exports.
 *  Sharing is a way of working with other people, and a pack of export credits
 *  says nothing about that. Video is unlocked by either because a layer type
 *  somebody already paid for should not be taken off them later; nobody has
 *  been sharing until now, so there is nothing to take away.
 *
 *  Asked on the way IN only. A licence that lapses does not unshare anything:
 *  the people a project was already given to keep working, and the owner can
 *  still see and withdraw those shares. Enforced in the database too — see
 *  0025_sharing_needs_licence.sql, which is what makes it true rather than
 *  merely displayed.
 *  @returns {{ok:boolean, reason?:string}} */
export function canShareProjects() {
  return motionActive() ? { ok: true } : { ok: false, reason: SHARE_LOCKED_REASON };
}

/* A PROJECT KEPT IN THE CLOUD is storage we pay for and keep, rather than a
   render that leaves and is somebody else's file afterwards — so it sits with
   the other things a credit pack unlocks. Any pack does it for 12 months (the
   same pack_access_until() clock video export reads, 0030), or a licence.

   THE GATE IS ON WRITING, NEVER ON READING. Somebody who saved projects while
   this was free keeps every one of them: they open, they export, they download,
   they can be deleted. What they cannot do is write a new one or save a change,
   and CLOUD_LOCKED_REASON is what says so. Taking away what is already stored
   would be a different and much worse decision than pricing new storage, and
   the policies in 0028 draw that line the same way — select and delete are
   untouched.

   Saving to a local folder or to this browser stays free and needs no account
   at all: services/projects-fs.js and services/projects-db.js. That is the
   honest answer to "then where do I put my work", and it is why this gate is
   about the cloud specifically rather than about saving. */
export const CLOUD_LOCKED_REASON =
  'Keeping projects in the cloud unlocks with any credit pack, for 12 months. Projects already '
  + 'there stay yours to open and export, and saving to your own computer is always free.';

/** Saving to the CLOUD. Saving to a local folder is free and needs no
 *  account at all — see services/projects-fs.js.
 *  @returns {{ok:boolean, reason?:string}} */
export function canCreateProject() {
  if (!hasRealAccount()) return { ok: false, reason: 'Sign in to save projects to the cloud.' };
  if (!(hasLicence() || hasActivePack())) {
    return { ok: false, reason: hasEverPurchased()
      ? 'Your credit pack’s 12 months have ended, so changes can’t be saved to the cloud. Projects '
        + 'already there stay yours to open and export; a new pack unlocks saving again.'
      : CLOUD_LOCKED_REASON };
  }
  return { ok: true };
}

/** IMPORTING OBJECTS from one of your cloud projects into the open scene — a
 *  credit-pack feature alongside cloud saving, on the same 12-month clock
 *  (0030), and offered on cloud projects only (ui/account.js), since it rides
 *  on keeping projects there.
 *
 *  Client-side on purpose, and a product line rather than a security one: the
 *  source project stays readable either way (0028 never gates reading — that
 *  is what keeps people's work reachable), so somebody without a pack could
 *  still open it and copy by hand. This gates the convenience, not the data.
 *  @returns {{ok:boolean, reason?:string}} */
export function canImportObjects() {
  const cloud = canCreateProject();
  if (cloud.ok) return { ok: true };
  if (!hasRealAccount()) return cloud;
  return { ok: false, reason: lapsedOr(
    'Importing objects from your other projects comes with any credit pack, for 12 months, '
    + 'alongside keeping projects in the cloud.') };
}

/** Writing to a project that is ALREADY in the cloud. Same answer as creating
 *  one — kept separate because the two refusals are read in different places
 *  and one of them has to survive being asked sixty times a minute by the
 *  autosave timer. */
export const canSaveToCloud = canCreateProject;
